import { useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { tournamentApi } from '../services/api'
import { useLanguage } from '../context/LanguageContext'
import LanguageToggle from '../components/LanguageToggle'

/**
 * Reseller "reset password" page — the destination of the emailed link.
 * Token is in the query string (?token=...). Posts to the shared
 * POST /api/auth/reset-password { token, password }.
 */
export default function ResetPassword() {
  const { t } = useLanguage()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') || ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    if (password.length < 8) { setError(t('sp_password_min_8', 'Password must be at least 8 characters.')); return }
    if (password !== confirm) { setError(t('sp_passwords_dont_match', 'Passwords do not match.')); return }
    setError(''); setSubmitting(true)
    try {
      await tournamentApi.post('/auth/reset-password', { token, password })
      setDone(true)
      setTimeout(() => navigate('/login'), 2500)
    } catch (err) {
      const data = err?.response?.data
      if (data?.errors?.length) setError(data.errors.join(' '))
      else setError(data?.message || err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Wrapper>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={eyebrow}>{t('sp_play_and_stay_portal_caps', 'PLAY AND STAY PORTAL')}</div>
        <h1 style={{ margin: 0, fontSize: 24, color: 'var(--secondary-950)' }}>{t('sp_choose_new_password', 'Choose a new password')}</h1>
      </div>

      {!token ? (
        <div style={errorBox}>{t('sp_reset_link_missing_token', 'This reset link is missing its token. Request a new one from the sign-in page.')}</div>
      ) : done ? (
        <div style={successBox}>{t('sp_password_reset_success', 'Your password has been reset. Redirecting you to sign in…')}</div>
      ) : (
        <form onSubmit={handleSubmit}>
          {error && <div style={errorBox}>{error}</div>}
          <label style={labelStyle}>{t('sp_new_password', 'New password')}</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} autoFocus style={inputStyle} placeholder="••••••••" />
          <label style={{ ...labelStyle, marginTop: 16 }}>{t('sp_confirm_password', 'Confirm password')}</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required style={inputStyle} placeholder="••••••••" />
          <button type="submit" disabled={submitting} className="btn btn-dark" style={{ width: '100%', marginTop: 24, padding: '14px', opacity: submitting ? 0.6 : 1 }}>
            {submitting ? t('sp_saving', 'Saving…') : t('sp_reset_password', 'Reset password')}
          </button>
        </form>
      )}

      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Link to="/login" style={backLink}>{t('sp_back_to_sign_in', '← Back to sign in')}</Link>
      </div>
    </Wrapper>
  )
}

function Wrapper({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--brand-50) 0%, #fff 50%, var(--brand-100) 100%)', padding: 20 }}>
      <div className="card" style={{ padding: 40, width: '100%', maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <LanguageToggle />
        </div>
        <Link to="/" style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <img src="/integolf-logo.svg" alt="InteGolf" style={{ height: 40 }} />
        </Link>
        {children}
      </div>
    </div>
  )
}

const eyebrow = { fontSize: 11, letterSpacing: 2, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 6 }
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--secondary-800)', marginBottom: 6 }
const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid var(--input-border)', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit', color: 'var(--text)', boxSizing: 'border-box' }
const successBox = { padding: 14, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 8, fontSize: 14, lineHeight: 1.5 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 14, fontSize: 13 }
const backLink = { fontSize: 14, color: 'var(--brand-600)', fontWeight: 600, textDecoration: 'none' }
